import React, { useState } from 'react';
import { Button } from '../../../components/ui/Button/Button';
import { Sheet } from '../../../components/technician/ui/Sheet';
import { TONE } from '../../../lib/rcu/catalog/feedback';
import { triggerOf } from '../encode';
import type { Keypad } from '../model';
import { erasePlan } from '../stored';
import { useKeypadStore } from '../store';
import { countOf } from './count';
import { eraseTriggers } from '../port';
import type { KeypadPort } from '../port';

export interface RemoveKeypadSheetProps {
  readonly isOpen: boolean;
  readonly keypad: Keypad;
  readonly port: KeypadPort;
  readonly onClose: () => void;
}

/**
 * Taking a panel out of the book, and optionally out of the device.
 *
 * The two are separate on purpose. Removing the panel here is this tablet's own
 * book-keeping and can be undone by adding it back; erasing the mappings on the
 * device cannot, and the device gives no undo. The sheet says how many mappings
 * the second half would delete — and only those this console knows hold
 * something, never a blanket erase of the address.
 */
export const RemoveKeypadSheet: React.FC<RemoveKeypadSheetProps> = ({
  isOpen,
  keypad,
  port,
  onClose,
}) => {
  const book = useKeypadStore((store) => store.book);
  const answers = useKeypadStore((store) => store.answers);
  const removeKeypad = useKeypadStore((store) => store.removeKeypad);
  const forgetWritten = useKeypadStore((store) => store.forgetWritten);
  const setNotice = useKeypadStore((store) => store.setNotice);
  const [busy, setBusy] = useState(false);

  const plan = erasePlan(book, keypad, answers);
  const name = keypad.name.trim() === '' ? 'This panel' : keypad.name;

  const remove = (alsoErase: boolean): void => {
    if (!alsoErase) {
      removeKeypad(keypad.id);
      onClose();
      return;
    }

    setBusy(true);
    void eraseTriggers({
      triggers: plan.map((ref) => triggerOf(ref.selector, ref.state)),
      port,
    })
      .then((outcome) => {
        if (outcome.ok) {
          forgetWritten(plan.map((ref) => ref.triggerKey));
          removeKeypad(keypad.id);
          setNotice({
            tone: TONE.SUCCESS,
            text: `Erased ${countOf(plan.length, 'mapping', 'mappings')} on the device, then removed ${name}.`,
          });
        } else {
          // Nothing is removed unless the erase was delivered: a half-done
          // removal would leave the console unable to say what the device holds.
          setNotice({
            tone: TONE.ERROR,
            text: `Nothing was erased or removed: ${outcome.detail}`,
          });
        }
      })
      .finally(() => {
        setBusy(false);
        onClose();
      });
  };

  return (
    <Sheet
      isOpen={isOpen}
      title={`Remove ${name}?`}
      subtitle="Removing a panel here does not touch the device unless you say so."
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="secondary" onClick={() => remove(false)} disabled={busy}>
            Remove from this tablet
          </Button>
          <Button variant="primary" onClick={() => remove(true)} disabled={busy || plan.length === 0}>
            {busy
              ? 'Erasing…'
              : `Erase ${countOf(plan.length, 'mapping', 'mappings')} and remove`}
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm text-[#666666]">
        <p>
          {name} and its {countOf(keypad.keys.length, 'key', 'keys')} leave this tablet&rsquo;s book.
          Nothing is sent to the RCU, and the panel can be added again later.
        </p>
        {plan.length > 0 ? (
          <>
            <p>
              The device is known to hold {countOf(plan.length, 'mapping', 'mappings')} for this
              panel — the ones this tablet wrote, and the ones it read back. Erasing sends one
              delete for each, in a single document: a panel has at most twelve triggers, well
              inside what the firmware will queue.
            </p>
            <ul className="space-y-0.5 font-mono text-xs text-[#333333]">
              {plan.map((ref) => (
                <li key={ref.triggerKey}>{ref.triggerKey}</li>
              ))}
            </ul>
          </>
        ) : (
          <p>
            Nothing is known to be stored on the device for this panel, so there is nothing to
            erase. Read the panel from the device first if you are not sure.
          </p>
        )}
      </div>
    </Sheet>
  );
};

export default RemoveKeypadSheet;
